import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarIcon, HeartIcon, HeartFilledIcon } from "./icons.jsx";
import { get, post } from "../lib/http.js";
import { API } from "../config/api.js";
import { absMediaUrl, formatEventScheduleLabel } from "../api/events.js";

const currencyINR = (n) =>
  typeof n === "number" ? `₹${n.toLocaleString("en-IN")}` : n || "—";

// Put this helper near your component (once)
const formatEventNoTZ = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";

  // Read the original calendar parts in UTC to avoid day rollover
  const y = d.getUTCFullYear();
  const m = d.getUTCMonth();       // 0-11
  const day = d.getUTCDate();      // 1-31
  const hh = d.getUTCHours();      // 0-23
  const mm = d.getUTCMinutes();    // 0-59

  // Build a date for formatting (date-only) and a manual time string
  const dateOnly = new Date(Date.UTC(y, m, day));
  const timeStr =
    `${((hh % 12) || 12)}:${String(mm).padStart(2, "0")} ${hh < 12 ? "AM" : "PM"}`;

  const dateStr = dateOnly.toLocaleDateString("en-IN", {
    weekday: "short",
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

  return `${dateStr} • ${timeStr}`;
};


export default function EventCardNew({
  item,
  showBookButton = true,
  compact = false,
  bodyBg = "white",
}) {
  const bodyBgClass = bodyBg === "yellow" ? "bg-amber-50/50 dark:bg-slate-800" : "bg-white dark:bg-slate-900";
  const navigate = useNavigate();
  const eventUrl = `/events/${item._id}`;

  const poster =
    absMediaUrl(item?.imageUrl || item?.landsacpImage?.[0]) || "";

  const [wishlisted, setWishlisted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [imgErr, setImgErr] = useState(false);

  useEffect(() => {
    setImgErr(false);
  }, [poster, item?._id]);

  useEffect(() => {
    const checkWishlist = async () => {
      try {
        const raw = localStorage.getItem("bb_user");
        const parsed = raw ? JSON.parse(raw) : null;
        const userId = parsed?.id;
        const token = parsed?.token;
        if (!userId || !token) return;

        const res = await get(`${API.wishlist.getWishlist}/${userId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const wishlistItems = res?.wishlist || res || {};
        const isWishlisted = wishlistItems?.eventIds?.some((obj) =>
          String(obj?._id ?? obj) === String(item._id)
        );
        setWishlisted(!!isWishlisted);
      } catch (err) {
        console.error("Error checking wishlist:", err);
      }
    };
    if (item?._id) checkWishlist();
  }, [item?._id]);

  const handleWishlistClick = async () => {
    if (loading) return;
    setLoading(true);

    try {
      const raw = localStorage.getItem("bb_user");
      const parsed = raw ? JSON.parse(raw) : null;
      const userId = parsed?.id;
      const token = parsed?.token;
      if (!userId || !token) return;

      if (wishlisted) {
        await post(
          `${API.wishlist.removeFromWishlist}`,
          { userId, eventId: item._id },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setWishlisted(false);
      } else {
        await post(
          API.wishlist.addToWishlist,
          { userId, eventIds: [item._id] },
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setWishlisted(true);
      }
    } catch (err) {
      console.error("Wishlist API error:", err);
    } finally {
      setLoading(false);
    }
  };

  const openEvent = () => {
    if (showBookButton && item?._id) navigate(eventUrl);
  };

  return (
    <div
      role={showBookButton ? "button" : undefined}
      tabIndex={showBookButton ? 0 : undefined}
      onClick={openEvent}
      onKeyDown={(e) => {
        if (showBookButton && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          openEvent();
        }
      }}
      className={`group flex h-full min-w-0 max-w-full flex-col overflow-hidden rounded-[20px] border border-slate-200/80 bg-white dark:bg-slate-900/80 dark:border-white/10 font-sans shadow-sm transition-all hover:border-amber-300 hover:shadow-lg dark:hover:border-slate-500 ${showBookButton ? "cursor-pointer" : ""}`}
    >
      <div className="relative overflow-hidden bg-slate-100 dark:bg-slate-800 aspect-[4/3] sm:aspect-auto">
        {poster && !imgErr ? (
          <img
            src={poster}
            alt=""
            className={`${compact ? "h-40" : "h-52"} w-full object-cover transition duration-500 group-hover:scale-105`}
            loading="lazy"
            onError={() => setImgErr(true)}
          />
        ) : (
          <div
            className={`flex w-full flex-col items-center justify-center bg-gradient-to-br from-amber-100 via-orange-50 to-slate-100 dark:from-slate-800 dark:via-slate-800 dark:to-slate-800 text-slate-500 dark:text-slate-400 ${compact ? "h-40" : "h-52"}`}
          >
            <span className="text-2xl" aria-hidden>
              🎟️
            </span>
            <span className="mt-1 px-3 text-center text-xs font-semibold">No poster</span>
          </div>
        )}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            handleWishlistClick();
          }}
          disabled={loading}
          className="absolute right-3 top-3 z-10 flex h-[36px] w-[36px] items-center justify-center rounded-full bg-white/80 dark:bg-black/40 backdrop-blur-md shadow-sm ring-1 ring-black/5 dark:ring-white/10 hover:bg-white dark:hover:bg-black/60 disabled:opacity-50 transition-all"
        >
          {wishlisted ? (
            <HeartFilledIcon className="h-5 w-5 text-red-500 drop-shadow-sm" />
          ) : (
            <HeartIcon className="h-5 w-5 text-slate-700 dark:text-white/90 drop-shadow-sm" />
          )}
        </button>
      </div>

      {/* Body */}
      <div className={`flex min-w-0 flex-1 flex-col rounded-b-[20px] ${bodyBgClass} p-3.5 sm:p-4 md:p-5`}>
        <h3
          className="line-clamp-2 text-[15px] md:text-base font-bold leading-snug tracking-tight text-slate-900 dark:text-slate-100 transition-colors"
          title={item.name}
        >
          {item.name}
        </h3>

        {item.venue && (
          <p
            className="mt-1.5 min-w-0 truncate text-[13px] font-medium text-slate-500 dark:text-slate-400"
            title={`${item.venue.address || ""} ${item.venue.city || ""}`.trim()}
          >
            {[item.venue.address, item.venue.city].filter(Boolean).join(", ")}
          </p>
        )}
        {(item.date || item.showtimes?.length) && (
          <div className="mt-2.5 flex min-w-0 items-center gap-2 text-[13px] font-medium text-slate-700 dark:text-slate-300">
            <CalendarIcon />
            <span className="min-w-0 truncate leading-snug">
              {item.showtimes?.length
                ? formatEventScheduleLabel(item)
                : formatEventNoTZ(item.date)}
            </span>
          </div>
        )}

        <div className="flex-1" />

        <div className="mt-4 flex flex-wrap items-end justify-between gap-x-3 gap-y-2 border-t border-slate-100 pt-3 dark:border-slate-800">
          <div className="min-w-0 flex flex-col items-start">
            <div className="mb-0.5 text-[9px] font-bold uppercase tracking-widest text-slate-500 dark:text-slate-400">
              Starting from
            </div>
            <div className="text-lg font-extrabold leading-none text-slate-900 dark:text-white md:text-xl">
              {(() => {
                if (!item.ticketCategories?.length) return "—";
                const minPrice = Math.min(...item.ticketCategories.map((t) => t.price));
                return minPrice === 0 ? "Free" : currencyINR(minPrice);
              })()}
            </div>
          </div>
          {showBookButton && (
            <span className="inline-flex shrink-0 items-center justify-center rounded-xl bg-amber-400 px-3.5 py-2 text-xs font-bold text-slate-950 shadow-sm transition-colors hover:bg-amber-500 sm:px-4 sm:py-2.5 sm:text-sm">
              Show details
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

